import * as React from 'react';
import { useRef, useState } from 'react';
import { Box, Button, Popover, Stack, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { ArrowRight, Info, Pill, Plus } from 'lucide-react';
import { ComparablePlanType } from '@coverright/data-access/types/medicare';
import { useNavigate } from 'react-router-dom';
import { getCompanyLogo, GetDefaultPlansYear } from '@coverright/utils';
import { CartItem, PlanCart } from '../plan-selection-types';
import { CartTile } from './checkout-cart-tile';
import { CrossSellBanner } from './checkout-cross-sell';
import { usePlanSelectionCtx } from '../plan-selection-context';
import { useAi } from '../../../../../shared/ai-context';
import { useMedicareQuote } from '../../../../../shared';
import { getRoute } from '../../../../../app/router';
import { PDPPlan } from '../../../../../shared/api';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CheckoutStep1Props {
  items: CartItem[];
  total: number;
  primaryType: ComparablePlanType | null;
  cart: PlanCart;
  onNextStep: () => void;
  onBrowse: () => void;
  onRemovePlan: (type: ComparablePlanType) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CheckoutStep1 = ({
  items, total, primaryType, cart, onNextStep, onRemovePlan, onBrowse
}: CheckoutStep1Props) => {
  const hasMG  = cart.mg  !== null;
  const hasPDP = cart.pdp !== null;

  const navigate   = useNavigate();
  const { selectPlan } = usePlanSelectionCtx();
  const { recommendations } = useAi();
  const quote = useMedicareQuote();

  const pdpPlans: PDPPlan[] = recommendations?.pdp?.recommendations ?? [];

  // ── PDP picker popover ────────────────────────────────────────────────────────
  const pdpBannerRef = useRef<HTMLDivElement>(null);
  const [pdpPopoverOpen, setPdpPopoverOpen] = useState(false);

  const openPdpPicker  = () => setPdpPopoverOpen(true);
  const closePdpPicker = () => setPdpPopoverOpen(false);

  const handlePdpSelect = (plan: PDPPlan) => {
    closePdpPicker();
    selectPlan({
      type: ComparablePlanType.Pdp,
      bidId: plan.bid_id,
      zip: quote?.zip ?? '',
      county: quote?.county ?? '',
      planName: plan.plan_name ?? '',
      orgName: plan.org_name ?? '',
      premium: plan.premium,
      deductible: plan.drug_deductible != null ? `$${plan.drug_deductible}` : undefined,
      logoUrl: getCompanyLogo(plan.org_name, plan.org_name),
      planYear: String(GetDefaultPlansYear()),
    });
  };

  const cheapestPdpBidId = (() => {
    const withCost = pdpPlans.filter(p => p.estimated_annual_cost != null);
    if (!withCost.length) return null;
    return withCost.reduce((a, b) =>
      (a.estimated_annual_cost ?? 0) <= (b.estimated_annual_cost ?? 0) ? a : b,
    ).bid_id;
  })();

  return (
    <Stack gap={4}>
      {/* Animated checkmark */}
      <Stack alignItems="center" gap={1.2}>
        <CheckmarkSvg />
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#101828', mb: 0.5 }}>
            Great choice!
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
            You're one step closer to your new coverage.
          </Typography>
        </Box>
      </Stack>

      {/* Cart tiles */}
      <Box>
        <Stack gap={3}>
          {items.map(item => (
            <CartTile
              key={item.type}
              item={item}
              isPrimary={item.type === primaryType}
              onRemove={() => onRemovePlan(item.type)}
            />
          ))}
        </Stack>

        {/* Total row (multiple items) */}
        {items.length > 1 && (
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, py: 1.5,
            bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 1,
            mt: 2,
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#101828' }}>
              Total Estimated Monthly Premium
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#009689' }}>
              ${total.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Cross-sell banners */}
      {hasMG && !hasPDP && (
        <>
          <div ref={pdpBannerRef}>
            <CrossSellBanner
              heading="Complete your coverage"
              body="Since you selected a Medicare Supplement plan, you may also want a Part D Drug Plan for prescription coverage."
              ctaLabel="Add Part D"
              ctaProps={{
                startIcon: <Pill size={14} color="#fff" />,
              }}
              browsePlansLabel="Browse Plans"
              onCta={openPdpPicker}
              onBrowse={() => {
                navigate(`${getRoute('plansAdvanced')}/pdp`);
                onBrowse();
              }}
            />
          </div>

          {/* PDP plan picker popover */}
          <Popover
            open={pdpPopoverOpen}
            anchorEl={pdpBannerRef.current}
            onClose={closePdpPicker}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{zIndex: 1600}}
            slotProps={{
              paper: {
                sx: {
                  width: 320,
                  borderRadius: '12px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                  border: '1px solid #e5e7eb',
                  mt: 0.5,
                  overflow: 'hidden',
                },
              },
            }}
          >
            <Box sx={{ p: 1 }} role="listbox">
              {pdpPlans.length === 0 && (
                <Typography sx={{ fontSize: 13, color: '#6b7280', p: 1.5 }}>
                  No PDP plans available.
                </Typography>
              )}
              {pdpPlans.map(plan => {
                const isLowest = plan.bid_id === cheapestPdpBidId;
                return (
                  <PdpPlanOption
                    key={plan.bid_id}
                    role="option"
                    onClick={() => handlePdpSelect(plan)}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 0.25 }}>
                        {plan.plan_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                        {plan.org_name}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                        ${plan.premium.toFixed(2)} /mo
                      </Typography>
                      {isLowest && (
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#059669' }}>
                          Lowest Cost
                        </Typography>
                      )}
                    </Box>
                  </PdpPlanOption>
                );
              })}
            </Box>
          </Popover>
        </>
      )}
      {hasPDP && !hasMG && (
        <CrossSellBanner
          heading="Consider adding Med Supplement"
          body="You've selected a Part D Drug Plan. Pairing it with a Medicare Supplement plan gives you comprehensive medical coverage."
          ctaLabel="Add Med Supp"
          ctaProps={{
            startIcon: <Plus size={14} color="#fff" />,
          }}
          browsePlansLabel="Browse Plans"
          onCta={() => {
            navigate(`${getRoute('plansAdvanced')}/mg`);
            onBrowse();
          }}
          onBrowse={() => {
            navigate(`${getRoute('plansAdvanced')}/mg`);
            onBrowse();
          }}
        />
      )}

      {/* CTA */}
      <Box sx={{ borderTop: '1px solid #f3f4f6', pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <NextStepsBtn onClick={onNextStep} sx={{width: {xs: 1, sm: 'fit-content'} }}>
          Continue to Next Steps
          <ArrowRight size={16} style={{ marginLeft: 8, flexShrink: 0 }} />
        </NextStepsBtn>
      </Box>
    </Stack>
  );
};

// ─── Animated SVG checkmark ───────────────────────────────────────────────────

const strokeCircle = keyframes`
  0%   { stroke-dashoffset: 166; }
  100% { stroke-dashoffset: 0; }
`;
const strokeCheck = keyframes`
  0%   { stroke-dashoffset: 48; }
  100% { stroke-dashoffset: 0; }
`;

const CirclePath = styled('circle')({
  strokeDasharray: 166,
  strokeDashoffset: 166,
  animation: `${strokeCircle} 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards`,
});
const CheckPath = styled('polyline')({
  strokeDasharray: 48,
  strokeDashoffset: 48,
  animation: `${strokeCheck} 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.6s forwards`,
});

const CheckmarkSvg = () => (
  <svg
    width={72} height={72}
    viewBox="0 0 52 52"
    fill="none"
    stroke="#10b981"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <CirclePath cx={26} cy={26} r={25} fill="none" />
    <CheckPath points="14 27 21 34 38 17" strokeWidth={4} fill="none" />
  </svg>
);

// ─── Styled ───────────────────────────────────────────────────────────────────

const PdpPlanOption = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '12px',
  marginBottom: 4,
  textAlign: 'left',
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 120ms',
  '&:hover': { background: '#f0fdfa', borderColor: '#ccfbf1' },
});

const NextStepsBtn = styled(Button)({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#101828',
  color: 'white',
  fontWeight: 700,
  fontSize: 16,
  height: 44,
  padding: '12px 24px',
  borderRadius: 10,
  border: 'none',
  cursor: 'pointer',
  transition: 'background-color 150ms',
  '&:hover': { backgroundColor: '#1f2937' },
});
